import Vue from 'vue'
import {mapGetters} from 'vuex'

import GoogleDirectionsAPI from 'js/services/google_directions_api'
import OTPDirectionsAPI from 'js/services/otp_directions_api'
import TimePicker from 'vue2-timepicker'
import SearchDirectionService from 'js/services/api/search-direction/search-direction'
import {
  buildLegacyPlannerDatepickerConfig,
  buildLegacyPlannerDirectionState,
  buildLegacyPlannerSearchError,
  buildLegacyPlannerTemporalReferenceParams,
  formatLegacyPlannerUserPosition,
  getLegacyPlannerLocationFieldSelection,
  getLegacyPlannerLocationValue,
  getLegacyPlannerToggledLocationField
} from 'js/lib/planner/legacy-search-form'
import {getMunicipalityBoundsCorners} from 'js/lib/planner/search-location'
import {buildLegacyPlannerFilteredRoutes, fetchLegacyPlannerRoutes} from 'js/lib/planner/legacy-search-routes'

import template from './_search-directions-form.pug'

const NO_RESULTS_ERROR = 'ZERO_RESULTS'
const PLANNER_MOTOR_ERROR = 'PLANNER_MOTOR_ERROR'

const PHOTON = 'photon'
const GOOGLE = 'google'

export default Vue.extend({
  template: template(),
  components: {
    TimePicker
  },
  props: {
    userPosition: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      // Delay
      time_delay: TIME_DELAY,
      min_char: MINCHAR,
      originDirection: '',
      destinationDirection: '',
      originCoordinates: '',
      destinationCoordinates: '',
      routes: null,
      locationField: null,
      searchError: null,
      temporalReference: 'now',
      hourReference: {
        HH: moment().format('HH'),
        mm: moment().format('mm')
      },
      dateReference: moment().startOf('day').toDate(),
      searchText: '', // El texto de búsqueda en el input
      selectedValue: '', // El valor seleccionado del dropdown
      dropdownVisible: false, // Variable para controlar la visibilidad del dropdown
      dropdownOptions: [] // Las opciones del dropdown obtenidas de la búsqueda
    }
  },
  computed: {
    ...mapGetters(['administrativeAreaTimeZone']),

    searchDisabled() {
      return !this.origin || !this.destination
    },
    origin() {
      return getLegacyPlannerLocationValue({
        coordinates: this.originCoordinates,
        direction: this.originDirection,
        locationField: this.locationField,
        field: 'origin',
        userPositionString: this.userPositionString
      })
    },
    destination() {
      return getLegacyPlannerLocationValue({
        coordinates: this.destinationCoordinates,
        direction: this.destinationDirection,
        locationField: this.locationField,
        field: 'destination',
        userPositionString: this.userPositionString
      })
    },
    userPositionString() {
      return formatLegacyPlannerUserPosition(this.userPosition)
    },
    temporalSelectOptions() {
      return [
        {label: this.$t('planner.timeModes.now'), value: 'now'},
        {label: this.$t('planner.timeModes.departure'), value: 'departure'},
        {label: this.$t('planner.timeModes.arrival'), value: 'arrival'}
      ]
    },
    showTimeDatePicker() {
      return this.temporalReference !== 'now'
    },
    formattedDate() {
      const formattedDate = moment(this.dateReference)
        .add(this.hourReference.HH, 'hours')
        .add(this.hourReference.mm, 'minutes')
        .toDate()

      return formattedDate
    },
    temporalReferenceParams() {
      return buildLegacyPlannerTemporalReferenceParams({
        showTimeDatePicker: this.showTimeDatePicker,
        temporalReference: this.temporalReference,
        formattedDate: this.formattedDate
      })
    },
    isMobileView() {
      return this.$store.state.device === 'mobile' || this.$store.state.device === 'tablet'
    },
    municipalityBounds() {
      const municipality = this.$municipality

      // LatLngBounds expects (southwest, northeast); without usable bounds a box
      // around the municipality center is used
      const {southWest, northEast} = getMunicipalityBoundsCorners({
        upperLeft: {lat: municipality.upper_left_bound_lat, lng: municipality.upper_left_bound_lng},
        lowerRight: {
          lat: municipality.lower_right_bound_lat,
          lng: municipality.lower_right_bound_lng
        },
        municipalityCenter: municipality.geo
      })

      return new google.maps.LatLngBounds(
        new google.maps.LatLng(southWest.lat, southWest.lng),
        new google.maps.LatLng(northEast.lat, northEast.lng)
      )
    },

    agenciesWhitelistUrls() {
      return this.currentAppConfig.agencies_whitelist
    },

    filteredRoutes() {
      const result = buildLegacyPlannerFilteredRoutes({
        routes: this.routes,
        motor: this.currentAppConfig.planner_motor,
        agenciesWhitelistUrls: this.agenciesWhitelistUrls
      })

      if (result.error) {
        this.handleError(result.error)
      }

      return result.routes
    }
  },
  watch: {
    filteredRoutes(newRoutes) {
      if (_.isEmpty(newRoutes)) {
        this.handleError(NO_RESULTS_ERROR)
      } else {
        this.searchError = null
      }

      this.$emit('routes', newRoutes)
    },
    searchError(newError) {
      this.$emit('search-error', newError)
    },
    userPosition(newValue) {
      if (!newValue) {
        this.locationField = null
      }
    }
  },
  mounted() {
    this.initSearchBoxes()
  },
  methods: {
    debounce(func, wait, immediate) {
      let timeout

      return function (...args) {
        const context = this
        const later = function () {
          timeout = null
          if (!immediate) func.apply(context, args)
        }
        const callNow = immediate && !timeout
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
        if (callNow) func.apply(context, args)
      }
    },
    initSearchBoxes() {
      this.initSearchBox('originDirection')
      this.initSearchBox('destinationDirection')
    },
    initSearchBox(targetInputName) {
      const router = this.currentAppConfig.planner_router
      if (router === GOOGLE || router === undefined) {
        // Direccionador de Google
        const searchBox = this.createSearchBoxGoogle(targetInputName)
        this.addPlacesChangedListener(targetInputName, searchBox)
      } else if (router === PHOTON) {
        // Direccionador de Photon
        this.createSearchBoxPhoton(targetInputName)
      } else {
        this.handleError(PLANNER_MOTOR_ERROR)
      }
    },
    createSearchBoxGoogle(targetInputName) {
      const targetInput = this.$refs[targetInputName].$refs.input

      return new google.maps.places.Autocomplete(targetInput, {
        bounds: this.municipalityBounds,
        fields: ['name', 'formatted_address', 'geometry']
      })
    },
    createSearchBoxPhoton(targetInputName) {
      const targetInput = this.$refs[targetInputName].$refs.input

      // Contenedor con las opciones de dirección bajo el inputTarget
      const dropdownContainer = document.createElement('div')
      dropdownContainer.className = 'dropdown-container'

      // Establecer estilos al contenedor del dropdown
      dropdownContainer.style.border = '1px solid #ccc'
      dropdownContainer.style.backgroundColor = '#fff'
      dropdownContainer.style.maxHeight = '200px'
      dropdownContainer.style.overflowY = 'auto'
      dropdownContainer.style.zIndex = '9999'
      dropdownContainer.style.display = 'none' // Para que inicialmente esté oculto

      // Establecer el ancho del dropdown para que sea igual al ancho del elemento inputTarget
      const targetRect = targetInput.getBoundingClientRect()
      dropdownContainer.style.width = `${targetRect.width}px`

      targetInput.addEventListener('keydown', this.debounce(() => {
        const {municipalityBounds} = this

        // Construir el objeto con los parámetros para el servicio
        const paramsBuscar = {
          paramsFilter: targetInput.value,
          routerUrl: this.currentAppConfig.planner_router_url,
          bbox: municipalityBounds,
          lat: this.$municipality && this.$municipality.geo ? this.$municipality.geo.lat : null,
          lon: this.$municipality && this.$municipality.geo ? this.$municipality.geo.lng : null,
          userPosition: this.userPosition
        }

        this.buscar(paramsBuscar).then(response => {
          const features = response.features

          if (features !== undefined && features.length > 0) {
            // Limpiar el contenido del dropdown antes de actualizarlo
            while (dropdownContainer.firstChild) {
              dropdownContainer.removeChild(dropdownContainer.firstChild)
            }

            // Crear y agregar las opciones del dropdown
            features.forEach(feature => {
              const option = document.createElement('div')

              // Establecer estilos para cada opción del dropdown
              option.style.padding = '8px'
              option.style.cursor = 'pointer'

              // Almacenar los valores de 'coordinates' internamente en el input
              const coordinates = feature.geometry.coordinates

              // Mostrar en el dropdown el nombre concatenado con 'locality' y 'city'
              const name = feature.properties.name
              const locality = feature.properties.locality
              const city = feature.properties.city
              let optionText = name
              if (locality !== undefined) {
                optionText += `, ${locality}`
              }
              if (city !== undefined) {
                optionText += `, ${city}`
              }
              option.textContent = optionText

              option.className = 'dropdown-option'
              option.addEventListener('click', () => {
                // Actualizar el valor seleccionado del dropdown
                let directionText = name
                if (locality !== undefined) {
                  directionText += `, ${locality}`
                }
                if (city !== undefined) {
                  directionText += `, ${city}`
                }

                if (targetInputName === 'originDirection') {
                  this.originDirection = directionText
                  this.originCoordinates = `${coordinates[1]},${coordinates[0]}`
                } else if (targetInputName === 'destinationDirection') {
                  this.destinationDirection = directionText
                  this.destinationCoordinates = `${coordinates[1]},${coordinates[0]}`
                }

                // Actualizar el valor del targetInput con el valor seleccionado
                targetInput.value = directionText

                // Ocultar desplegable tras terminar todo lo necesario al hacer click
                dropdownContainer.style.display = 'none'
              })
              dropdownContainer.appendChild(option)
            })

            // Posicionar y mostrar el dropdown sobre el targetInput
            dropdownContainer.style.position = 'absolute'
            dropdownContainer.style.top = `${targetRect.bottom}px`
            dropdownContainer.style.left = `${targetRect.left}px`
            dropdownContainer.style.display = 'block'

            document.body.appendChild(dropdownContainer)
          } else {
            dropdownContainer.style.display = 'none'
          }
        })
      }, this.time_delay).bind(this))
    },
    addPlacesChangedListener(modelName, searchBox) {
      // Cuando se selecciona una dirección del listado de Google salta este evento
      google.maps.event.addListener(searchBox, 'place_changed', this.debounce(() => {
        const place = searchBox.getPlace() // Obtener el lugar seleccionado

        if (place) {
          // Establecer datos de la dirección en el input
          this.setDirection(modelName, place)
        }
      }, this.time_delay))
    },
    setDirection(fieldName, place) {
      Object.assign(this, buildLegacyPlannerDirectionState(fieldName, place))
    },
    setLocationField(target) {
      this.locationField = getLegacyPlannerLocationFieldSelection(target, this.userPosition, this.locationField)
    },
    toggleMyLocation() {
      this.locationField = getLegacyPlannerToggledLocationField(this.locationField)
    },
    async searchRoutes() {
      const {origin, destination, originDirection, destinationDirection} = this
      this.searchError = null
      this.routes = []

      Vue.store.commit('incrementLoading')
      try {
        this.routes = await fetchLegacyPlannerRoutes({
          motor: this.currentAppConfig.planner_motor,
          googleDirectionsApi: GoogleDirectionsAPI,
          otpDirectionsApi: OTPDirectionsAPI,
          temporalReferenceParams: this.temporalReferenceParams,
          origin,
          destination,
          administrativeAreaTimeZone: this.$administrativeAreaTimeZone,
          otpUrl: this.currentAppConfig.planner_motor_url,
          originDirection,
          destinationDirection
        })
      } catch (error) {
        this.handleError(error)
      }

      Vue.store.commit('decreaseLoading')
    },
    handleError(error) {
      this.searchError = this.getSearchError(error)
    },
    getSearchError(error) {
      return buildLegacyPlannerSearchError(error, {
        hasTranslation: this.$te,
        translate: this.$t,
        camelCase: _.camelCase
      })
    },
    toggleDirections() {
      const originDirection = this.originDirection
      this.originDirection = this.destinationDirection
      this.destinationDirection = originDirection

      const originCoordinates = this.originCoordinates
      this.originCoordinates = this.destinationCoordinates
      this.destinationCoordinates = originCoordinates

      this.toggleMyLocation()
    },
    datepickerObjectConfig() {
      return buildLegacyPlannerDatepickerConfig()
    },
    async buscar(params) {
      try {
        const response = await SearchDirectionService.get(params)
        if (_.isEmpty(response.body)) {
          this.handleError(NO_RESULTS_ERROR)
          return Promise.reject(new Error('No se encontraron resultados'))
        }

        return Promise.resolve(response.body)
      } catch (error) {
        this.handleError(error)
        return Promise.reject(error)
      }
    }
  }
})
